// src/pages/AdminOrdersVouchers.tsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  FaBan,
  FaCheckCircle,
  FaClock,
  FaCreditCard,
  FaExclamationTriangle,
  FaEye,
  FaFileInvoiceDollar,
  FaPaperPlane,
  FaRedo,
  FaSearch,
  FaShieldAlt,
  FaTicketAlt,
  FaTimesCircle,
  FaWrench,
} from 'react-icons/fa';
import { supabase } from '../services/supabaseClient';
import { useTransactionI18n } from '../lib/transactionI18n';
import ForfeoFulfillmentReadinessPanel from '../components/network/ForfeoFulfillmentReadinessPanel';

type DashboardStats = {
  paid_orders?: number;
  pending_orders?: number;
  active_vouchers?: number;
  redeemed_vouchers?: number;
  expired_vouchers?: number;
  cancelled_vouchers?: number;
  redemption_logs?: number;
  anomalies_count?: number;
};

type AdminOrderRow = {
  id?: string;
  status?: string | null;
  payment_status?: string | null;
  fulfillment_status?: string | null;
  customer_name?: string | null;
  customer_email?: string | null;
  business_id?: string | null;
  total_amount?: number | string | null;
  total_cents?: number | null;
  amount_cents?: number | null;
  currency?: string | null;
  stripe_session_id?: string | null;
  stripe_payment_intent_id?: string | null;
  paid_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

type AdminVoucherRow = {
  id?: string;
  order_id?: string | null;
  order_item_id?: string | null;
  business_id?: string | null;
  voucher_code?: string | null;
  status?: string | null;
  customer_email?: string | null;
  title?: string | null;
  amount_cents?: number | null;
  currency?: string | null;
  expires_at?: string | null;
  redeemed_at?: string | null;
  redeemed_by?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

type RedemptionRow = {
  id?: string;
  voucher_id?: string | null;
  order_id?: string | null;
  business_id?: string | null;
  voucher_code?: string | null;
  previous_status?: string | null;
  new_status?: string | null;
  source?: string | null;
  redeemed_by?: string | null;
  redeemed_at?: string | null;
  created_at?: string | null;
};

type AnomalyRow = {
  type?: string;
  severity?: 'info' | 'warning' | 'critical' | string;
  message?: string;
  order_id?: string | null;
  voucher_id?: string | null;
  voucher_code?: string | null;
  customer_email?: string | null;
  created_at?: string | null;
};

type DashboardResponse = {
  ok?: boolean;
  generated_at?: string;
  stats?: DashboardStats;
  recent_orders?: AdminOrderRow[];
  recent_vouchers?: AdminVoucherRow[];
  recent_redemptions?: RedemptionRow[];
  anomalies?: AnomalyRow[];
  error?: string;
};


type AdminActionResponse = {
  ok?: boolean;
  error?: string;
  details?: string;
  message?: string;
  action?: string;
  order?: AdminOrderRow | null;
  items?: any[];
  vouchers?: AdminVoucherRow[];
  redemption_logs?: RedemptionRow[];
  admin_actions?: any[];
  created_vouchers?: AdminVoucherRow[];
  voucher?: AdminVoucherRow | null;
  email_id?: string | null;
};

type OrderDetails = {
  order?: AdminOrderRow | null;
  items?: any[];
  vouchers?: AdminVoucherRow[];
  redemption_logs?: RedemptionRow[];
  admin_actions?: any[];
};

const moneyFormatter = new Intl.NumberFormat('fr-CA', {
  style: 'currency',
  currency: 'CAD',
});

const dateFormatter = new Intl.DateTimeFormat('fr-CA', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

const compactDateFormatter = new Intl.DateTimeFormat('fr-CA', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const formatDate = (value?: string | null, compact = false) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return compact ? compactDateFormatter.format(parsed) : dateFormatter.format(parsed);
};

const formatAmount = (row: { total_cents?: number | null; amount_cents?: number | null; amount_cents_alt?: number | null; total_amount?: number | string | null; amount_cents_voucher?: number | null }) => {
  const cents = row.total_cents ?? row.amount_cents ?? row.amount_cents_alt ?? row.amount_cents_voucher;
  if (typeof cents === 'number' && Number.isFinite(cents)) return moneyFormatter.format(cents / 100);

  const totalAmount = typeof row.total_amount === 'string' ? Number(row.total_amount) : row.total_amount;
  if (typeof totalAmount === 'number' && Number.isFinite(totalAmount)) return moneyFormatter.format(totalAmount);

  return '—';
};

const normalize = (value?: string | null) => String(value || '').toLowerCase();

const isPaidLike = (order?: AdminOrderRow | null) => {
  const status = normalize(order?.status);
  const payment = normalize(order?.payment_status);
  return status === 'paid' || status === 'completed' || payment === 'paid' || payment === 'succeeded';
};

const canCancelVoucher = (voucher?: AdminVoucherRow | null) => {
  const status = normalize(voucher?.status);
  return Boolean(voucher?.id) && !['redeemed', 'cancelled', 'canceled', 'void'].includes(status);
};

const statusPill = (status?: string | null) => {
  const s = normalize(status);
  if (s.includes('paid') || s === 'active' || s === 'redeemed' || s === 'success') {
    return 'bg-green-50 text-green-700 border-green-100';
  }
  if (s.includes('pending') || s.includes('attente')) {
    return 'bg-amber-50 text-amber-700 border-amber-100';
  }
  if (s.includes('cancel') || s.includes('fail') || s.includes('expired') || s.includes('error')) {
    return 'bg-red-50 text-red-700 border-red-100';
  }
  return 'bg-slate-50 text-slate-600 border-slate-100';
};

const shortId = (value?: string | null) => {
  if (!value) return '—';
  return value.length > 12 ? `${value.slice(0, 8)}…${value.slice(-4)}` : value;
};

const StatCard = ({
  icon,
  label,
  value,
  hint,
  tone = 'slate',
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  hint?: string;
  tone?: 'slate' | 'blue' | 'green' | 'amber' | 'red' | 'purple';
}) => {
  const tones: Record<string, string> = {
    slate: 'bg-slate-100 text-slate-700',
    blue: 'bg-blue-100 text-blue-700',
    green: 'bg-green-100 text-green-700',
    amber: 'bg-amber-100 text-amber-700',
    red: 'bg-red-100 text-red-700',
    purple: 'bg-purple-100 text-purple-700',
  };

  return (
    <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm flex items-start gap-4">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${tones[tone]}`}>{icon}</div>
      <div className="min-w-0">
        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{label}</p>
        <p className="text-2xl font-black text-slate-900 mt-1">{value}</p>
        {hint && <p className="text-xs text-slate-500 font-bold mt-1">{hint}</p>}
      </div>
    </div>
  );
};

const SectionCard = ({
  title,
  subtitle,
  children,
  right,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  right?: React.ReactNode;
}) => (
  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
    <div className="p-5 border-b border-slate-100 bg-slate-50 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
      <div>
        <h3 className="font-black text-slate-900">{title}</h3>
        {subtitle && <p className="text-xs text-slate-500 font-bold mt-1">{subtitle}</p>}
      </div>
      {right}
    </div>
    <div>{children}</div>
  </div>
);

const EmptyState = ({ text }: { text: string }) => (
  <div className="p-10 text-center text-slate-400 font-bold border-2 border-dashed border-slate-100 rounded-2xl m-5">{text}</div>
);

function AdminOrdersVouchers() {
  const { tx, orderStatus, voucherStatus, scanSource } = useTransactionI18n();
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const loadDashboard = async (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError(null);

    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;

      if (sessionError) {
        throw new Error(sessionError.message || 'Impossible de vérifier la session admin.');
      }

      if (!accessToken) {
        throw new Error('Session admin introuvable. Déconnecte-toi puis reconnecte-toi au portail admin.');
      }

      const supabaseUrl = ((import.meta as any).env?.VITE_SUPABASE_URL || '').trim();
      const supabaseAnonKey = ((import.meta as any).env?.VITE_SUPABASE_ANON_KEY || '').trim();

      if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error('Configuration Supabase manquante côté frontend : VITE_SUPABASE_URL ou VITE_SUPABASE_ANON_KEY.');
      }

      const res = await fetch(`${supabaseUrl}/functions/v1/admin-orders-vouchers`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          apikey: supabaseAnonKey,
          'Content-Type': 'application/json',
          'x-client-info': 'forfeo-admin-orders-vouchers',
        },
        body: JSON.stringify({ limit: 75 }),
      });

      const raw = await res.text();
      let response: DashboardResponse | null = null;

      try {
        response = raw ? JSON.parse(raw) : null;
      } catch (_parseError) {
        response = null;
      }

      if (!res.ok) {
        const serverError = response?.error || raw || `HTTP ${res.status}`;
        const details = (response as any)?.details ? ` — ${(response as any).details}` : '';
        const email = (response as any)?.email ? ` — email reçu: ${(response as any).email}` : '';
        throw new Error(`Edge Function admin-orders-vouchers refusée (${res.status}) : ${serverError}${details}${email}`);
      }

      if (!response?.ok) {
        throw new Error(response?.error || 'Réponse admin invalide.');
      }

      setData(response);
    } catch (err: any) {
      setError(err?.message || 'Erreur inconnue.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };



  const callAdminAction = async (payload: Record<string, unknown>, options?: { refresh?: boolean; silentSuccess?: boolean }) => {
    const action = String(payload.action || 'action');
    setActionLoading(action);
    setActionMessage(null);

    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (sessionError) throw new Error(sessionError.message || 'Impossible de vérifier la session admin.');
      if (!accessToken) throw new Error('Session admin introuvable. Déconnecte-toi puis reconnecte-toi au portail admin.');

      const supabaseUrl = ((import.meta as any).env?.VITE_SUPABASE_URL || '').trim();
      const supabaseAnonKey = ((import.meta as any).env?.VITE_SUPABASE_ANON_KEY || '').trim();
      if (!supabaseUrl || !supabaseAnonKey) throw new Error('Configuration Supabase manquante côté frontend.');

      const res = await fetch(`${supabaseUrl}/functions/v1/admin-order-action`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          apikey: supabaseAnonKey,
          'Content-Type': 'application/json',
          'x-client-info': 'forfeo-admin-order-action',
        },
        body: JSON.stringify(payload),
      });

      const raw = await res.text();
      let response: AdminActionResponse | null = null;
      try {
        response = raw ? JSON.parse(raw) : null;
      } catch (_parseError) {
        response = null;
      }

      if (!res.ok || !response?.ok) {
        const serverError = response?.error || raw || `HTTP ${res.status}`;
        const details = response?.details ? ` — ${response.details}` : '';
        throw new Error(`Action admin refusée (${res.status}) : ${serverError}${details}`);
      }

      if (!options?.silentSuccess) {
        setActionMessage({ type: 'success', text: response.message || 'Action admin exécutée avec succès.' });
      }

      if (options?.refresh !== false) await loadDashboard(true);
      return response;
    } catch (err: any) {
      const text = err?.message || 'Erreur admin inconnue.';
      setActionMessage({ type: 'error', text });
      throw err;
    } finally {
      setActionLoading(null);
    }
  };

  const handleResendReceipt = async (order: AdminOrderRow) => {
    if (!order.id) return;
    if (!order.customer_email) {
      setActionMessage({ type: 'error', text: 'Impossible de renvoyer le reçu : cette commande n’a pas d’email client.' });
      return;
    }
    if (!window.confirm(`Renvoyer le reçu et les liens de vouchers à ${order.customer_email} ?`)) return;
    await callAdminAction({ action: 'resend_receipt', order_id: order.id });
  };

  const handleRegenerateVouchers = async (orderId?: string | null) => {
    if (!orderId) return;
    if (!window.confirm('Régénérer les vouchers manquants pour cette commande payée ? Les vouchers existants ne seront pas dupliqués.')) return;
    await callAdminAction({ action: 'regenerate_vouchers', order_id: orderId });
  };

  const handleCancelVoucher = async (voucher: AdminVoucherRow) => {
    if (!voucher.id) return;
    const reason = window.prompt(`Raison d’annulation du voucher ${voucher.voucher_code || shortId(voucher.id)} ?`, 'Annulation administrative Forfeo');
    if (reason === null) return;
    await callAdminAction({ action: 'cancel_voucher', voucher_id: voucher.id, reason });
  };

  const handleOpenOrderDetails = async (orderId?: string | null) => {
    if (!orderId) return;
    const response = await callAdminAction({ action: 'order_details', order_id: orderId }, { refresh: false, silentSuccess: true });
    setOrderDetails({
      order: response.order || null,
      items: response.items || [],
      vouchers: response.vouchers || [],
      redemption_logs: response.redemption_logs || [],
      admin_actions: response.admin_actions || [],
    });
    setDetailsOpen(true);
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const stats = data?.stats || {};
  const q = search.trim().toLowerCase();

  const filteredOrders = useMemo(() => {
    const rows = data?.recent_orders || [];
    if (!q) return rows;
    return rows.filter((row) =>
      [row.id, row.customer_email, row.customer_name, row.status, row.payment_status, row.stripe_session_id, row.stripe_payment_intent_id]
        .map((v) => String(v || '').toLowerCase())
        .some((v) => v.includes(q))
    );
  }, [data?.recent_orders, q]);

  const filteredVouchers = useMemo(() => {
    const rows = data?.recent_vouchers || [];
    if (!q) return rows;
    return rows.filter((row) =>
      [row.id, row.order_id, row.customer_email, row.voucher_code, row.status, row.title]
        .map((v) => String(v || '').toLowerCase())
        .some((v) => v.includes(q))
    );
  }, [data?.recent_vouchers, q]);

  const filteredRedemptions = useMemo(() => {
    const rows = data?.recent_redemptions || [];
    if (!q) return rows;
    return rows.filter((row) =>
      [row.id, row.order_id, row.voucher_id, row.voucher_code, row.source, row.new_status, row.redeemed_by]
        .map((v) => String(v || '').toLowerCase())
        .some((v) => v.includes(q))
    );
  }, [data?.recent_redemptions, q]);

  const filteredAnomalies = useMemo(() => {
    const rows = data?.anomalies || [];
    if (!q) return rows;
    return rows.filter((row) =>
      [row.type, row.message, row.order_id, row.voucher_id, row.voucher_code, row.customer_email]
        .map((v) => String(v || '').toLowerCase())
        .some((v) => v.includes(q))
    );
  }, [data?.anomalies, q]);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center shadow-sm">
        <div className="w-12 h-12 mx-auto rounded-full border-4 border-blue-100 border-t-blue-600 animate-spin mb-4" />
        <p className="font-black text-slate-900">{tx('loadingAdminCenter')}</p>
        <p className="text-xs text-slate-500 font-bold mt-1">{tx('adminPaymentsSubtitle')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl border border-red-100 p-8 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-red-100 text-red-600 flex items-center justify-center shrink-0">
            <FaExclamationTriangle />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-black text-slate-900">{tx('centerUnavailable')}</h2>
            <p className="text-sm text-slate-600 font-bold mt-2">{error}</p>
            <p className="text-xs text-slate-400 mt-3">
              Vérifie que la fonction Supabase <span className="font-mono font-black">admin-orders-vouchers</span> est déployée et que ton compte possède un rôle admin.
            </p>
            <button
              onClick={() => loadDashboard()}
              className="mt-5 inline-flex items-center gap-2 bg-slate-900 text-white px-5 py-3 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-slate-800 transition"
            >
              <FaRedo /> {tx('retry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-in fade-in space-y-8">
      <div className="relative overflow-hidden rounded-3xl bg-slate-950 text-white p-6 md:p-8 shadow-xl">
        <div className="absolute -top-20 -right-16 w-60 h-60 rounded-full bg-blue-500/20 blur-3xl" />
        <div className="absolute -bottom-24 -left-16 w-64 h-64 rounded-full bg-emerald-500/20 blur-3xl" />
        <div className="relative flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/10 text-[10px] font-black uppercase tracking-widest text-blue-100 mb-4">
              <FaShieldAlt /> {tx('adminPaymentsTitle')}
            </div>
            <h2 className="text-2xl md:text-4xl font-black tracking-tight">{tx('adminPaymentsTitle')}</h2>
            <p className="text-sm md:text-base text-slate-300 mt-3 max-w-3xl font-medium leading-relaxed">
              {tx('adminPaymentsSubtitle')}
            </p>
            <p className="text-[11px] text-slate-400 mt-3 font-bold">{tx('latestSync')} : {formatDate(data?.generated_at)}</p>
          </div>
          <button
            onClick={() => loadDashboard(true)}
            disabled={refreshing}
            className="bg-white text-slate-950 px-5 py-3 rounded-2xl font-black text-xs uppercase tracking-widest inline-flex items-center justify-center gap-2 hover:bg-blue-50 transition disabled:opacity-60"
          >
            <FaRedo className={refreshing ? 'animate-spin' : ''} /> {tx('refresh')}
          </button>
        </div>
      </div>

      <ForfeoFulfillmentReadinessPanel
        context="orders"
        total={(stats.paid_orders || 0) + (stats.pending_orders || 0)}
        confirmed={stats.paid_orders || 0}
        completed={stats.redeemed_vouchers || 0}
        anomalies={stats.anomalies_count || 0}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        <StatCard icon={<FaCreditCard />} label={tx('paidOrdersStat')} value={stats.paid_orders || 0} tone="green" hint="Stripe confirmé / statut payé" />
        <StatCard icon={<FaClock />} label={tx('pendingOrdersStat')} value={stats.pending_orders || 0} tone="amber" hint="Paiement non confirmé" />
        <StatCard icon={<FaTicketAlt />} label={tx('activeVouchersStat')} value={stats.active_vouchers || 0} tone="blue" hint="Billets utilisables" />
        <StatCard icon={<FaCheckCircle />} label={tx('redeemedVouchersStat')} value={stats.redeemed_vouchers || 0} tone="purple" hint={`${stats.redemption_logs || 0} entrées d'audit`} />
        <StatCard icon={<FaTimesCircle />} label={`${tx('expired')} ${tx('vouchers')}`} value={stats.expired_vouchers || 0} tone="red" hint="Actifs mais date dépassée" />
        <StatCard icon={<FaTimesCircle />} label={`${tx('cancelled')} ${tx('vouchers')}`} value={stats.cancelled_vouchers || 0} tone="slate" hint="Annulations système/admin" />
        <StatCard icon={<FaExclamationTriangle />} label={tx('anomalies')} value={stats.anomalies_count || 0} tone={(stats.anomalies_count || 0) > 0 ? 'red' : 'green'} hint={tx('checkBeforeSupport')} />
        <StatCard icon={<FaFileInvoiceDollar />} label={tx('recentViews')} value={(data?.recent_orders || []).length + (data?.recent_vouchers || []).length} tone="slate" hint={tx('ordersVouchersLoaded')} />
      </div>

      <div className="bg-white p-4 rounded-2xl border border-slate-200 flex items-center gap-3 shadow-sm">
        <FaSearch className="text-slate-400 ml-2" />
        <input
          type="text"
          placeholder={tx('searchPlaceholderAdmin')}
          className="w-full outline-none font-bold text-slate-700 text-sm"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {actionMessage && (
        <div className={`rounded-2xl border p-4 flex items-start gap-3 ${actionMessage.type === 'success' ? 'bg-green-50 border-green-100 text-green-800' : 'bg-red-50 border-red-100 text-red-800'}`}>
          <div className="mt-0.5">{actionMessage.type === 'success' ? <FaCheckCircle /> : <FaExclamationTriangle />}</div>
          <div className="flex-1">
            <p className="font-black text-sm">{actionMessage.type === 'success' ? tx('actionCompleted') : tx('actionImpossible')}</p>
            <p className="text-sm font-bold mt-1">{actionMessage.text}</p>
          </div>
          <button onClick={() => setActionMessage(null)} className="text-xs font-black uppercase opacity-70 hover:opacity-100">{tx('close')}</button>
        </div>
      )}

      <SectionCard
        title={tx('anomaliesToCheck')}
        subtitle={tx('anomaliesSubtitle')}
        right={<span className={`px-3 py-1 rounded-full text-xs font-black border ${filteredAnomalies.length ? 'bg-red-50 text-red-700 border-red-100' : 'bg-green-50 text-green-700 border-green-100'}`}>{filteredAnomalies.length} {tx('itemsCount')}</span>}
      >
        {filteredAnomalies.length === 0 ? (
          <EmptyState text={tx('noAnomaly')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[1080px]">
              <thead className="bg-slate-50 text-slate-500 border-b border-slate-100">
                <tr>
                  <th className="p-4 text-xs font-black uppercase">{tx('severity')}</th>
                  <th className="p-4 text-xs font-black uppercase">{tx('type')}</th>
                  <th className="p-4 text-xs font-black uppercase">{tx('message')}</th>
                  <th className="p-4 text-xs font-black uppercase">{tx('order')}</th>
                  <th className="p-4 text-xs font-black uppercase">{tx('voucher')}</th>
                  <th className="p-4 text-xs font-black uppercase">{tx('customer')}</th>
                  <th className="p-4 text-xs font-black uppercase text-right">{tx('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredAnomalies.map((row, index) => (
                  <tr key={`${row.type}-${row.order_id}-${row.voucher_id}-${index}`} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="p-4">
                      <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase ${normalize(row.severity) === 'critical' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                        {row.severity || 'warning'}
                      </span>
                    </td>
                    <td className="p-4 font-black text-slate-800 text-sm">{row.type || '—'}</td>
                    <td className="p-4 text-sm text-slate-600 font-bold max-w-md">{row.message || '—'}</td>
                    <td className="p-4 font-mono text-xs text-slate-500">{shortId(row.order_id)}</td>
                    <td className="p-4"><div className="font-mono text-xs font-black text-slate-700">{row.voucher_code || shortId(row.voucher_id)}</div></td>
                    <td className="p-4 text-xs font-bold text-slate-500">{row.customer_email || '—'}</td>
                    <td className="p-4 text-right">
                      <div className="flex justify-end gap-2">
                        {row.order_id && (
                          <button
                            onClick={() => handleOpenOrderDetails(row.order_id)}
                            className="inline-flex items-center gap-1 bg-white border border-slate-200 text-slate-700 px-3 py-2 rounded-xl text-[10px] font-black uppercase hover:bg-slate-50"
                          >
                            <FaEye /> {tx('details')}
                          </button>
                        )}
                        {row.type === 'paid_order_without_voucher' && row.order_id && (
                          <button
                            onClick={() => handleRegenerateVouchers(row.order_id)}
                            disabled={Boolean(actionLoading)}
                            className="inline-flex items-center gap-1 bg-blue-600 text-white px-3 py-2 rounded-xl text-[10px] font-black uppercase hover:bg-blue-700 disabled:opacity-50"
                          >
                            <FaWrench /> {tx('regenerate')}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <SectionCard title={tx('latestOrders')} subtitle={tx('latestOrdersSubtitle')}>
        {filteredOrders.length === 0 ? (
          <EmptyState text={tx('noOrderFound')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[1120px]">
              <thead className="bg-slate-50 text-slate-500 border-b border-slate-100">
                <tr>
                  <th className="p-4 text-xs font-black uppercase">{tx('date')}</th>
                  <th className="p-4 text-xs font-black uppercase">{tx('customer')}</th>
                  <th className="p-4 text-xs font-black uppercase">{tx('amount')}</th>
                  <th className="p-4 text-xs font-black uppercase">{tx('payment')}</th>
                  <th className="p-4 text-xs font-black uppercase">{tx('fulfillment')}</th>
                  <th className="p-4 text-xs font-black uppercase">Stripe</th>
                  <th className="p-4 text-xs font-black uppercase text-right">ID</th>
                  <th className="p-4 text-xs font-black uppercase text-right">{tx('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((order) => (
                  <tr key={order.id} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="p-4 text-xs font-bold text-slate-500">{formatDate(order.created_at, true)}</td>
                    <td className="p-4">
                      <div className="font-black text-slate-800 text-sm">{order.customer_name || 'Client Forfeo'}</div>
                      <div className="text-xs text-slate-400 font-bold">{order.customer_email || '—'}</div>
                    </td>
                    <td className="p-4 font-black text-green-700">{formatAmount(order)}</td>
                    <td className="p-4">
                      <span className={`px-3 py-1 rounded-full border text-[10px] font-black uppercase ${statusPill(order.payment_status || order.status)}`}>
                        {orderStatus(order.payment_status || order.status) || '—'}
                      </span>
                    </td>
                    <td className="p-4 text-xs font-bold text-slate-500">{order.fulfillment_status || '—'}</td>
                    <td className="p-4">
                      <div className="font-mono text-[10px] text-slate-500">Session: {shortId(order.stripe_session_id)}</div>
                      <div className="font-mono text-[10px] text-slate-500">PI: {shortId(order.stripe_payment_intent_id)}</div>
                    </td>
                    <td className="p-4 text-right font-mono text-xs text-slate-500">{shortId(order.id)}</td>
                    <td className="p-4 text-right">
                      <div className="flex flex-wrap justify-end gap-2">
                        <button
                          onClick={() => handleOpenOrderDetails(order.id)}
                          className="inline-flex items-center gap-1 bg-white border border-slate-200 text-slate-700 px-3 py-2 rounded-xl text-[10px] font-black uppercase hover:bg-slate-50"
                        >
                          <FaEye /> {tx('details')}
                        </button>
                        {isPaidLike(order) && (
                          <button
                            onClick={() => handleResendReceipt(order)}
                            disabled={Boolean(actionLoading) || !order.customer_email}
                            className="inline-flex items-center gap-1 bg-slate-900 text-white px-3 py-2 rounded-xl text-[10px] font-black uppercase hover:bg-slate-800 disabled:opacity-50"
                          >
                            <FaPaperPlane /> {tx('receipt')}
                          </button>
                        )}
                        {isPaidLike(order) && (
                          <button
                            onClick={() => handleRegenerateVouchers(order.id)}
                            disabled={Boolean(actionLoading)}
                            className="inline-flex items-center gap-1 bg-blue-600 text-white px-3 py-2 rounded-xl text-[10px] font-black uppercase hover:bg-blue-700 disabled:opacity-50"
                          >
                            <FaWrench /> Vouchers
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <SectionCard title={tx('latestVouchers')} subtitle="Billets générés après paiement Stripe.">
        {filteredVouchers.length === 0 ? (
          <EmptyState text={tx('noVoucherFound')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[1100px]">
              <thead className="bg-slate-50 text-slate-500 border-b border-slate-100">
                <tr>
                  <th className="p-4 text-xs font-black uppercase">Code</th>
                  <th className="p-4 text-xs font-black uppercase">Client / Titre</th>
                  <th className="p-4 text-xs font-black uppercase">{tx('amount')}</th>
                  <th className="p-4 text-xs font-black uppercase">{tx('status')}</th>
                  <th className="p-4 text-xs font-black uppercase">Expiration</th>
                  <th className="p-4 text-xs font-black uppercase">Utilisation</th>
                  <th className="p-4 text-xs font-black uppercase text-right">{tx('order')}</th>
                  <th className="p-4 text-xs font-black uppercase text-right">{tx('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredVouchers.map((voucher) => (
                  <tr key={voucher.id} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="p-4">
                      <div className="font-mono font-black text-slate-900">{voucher.voucher_code || '—'}</div>
                      <div className="text-[10px] font-mono text-slate-400">{shortId(voucher.id)}</div>
                    </td>
                    <td className="p-4">
                      <div className="font-black text-slate-800 text-sm">{voucher.title || 'Voucher Forfeo'}</div>
                      <div className="text-xs text-slate-400 font-bold">{voucher.customer_email || '—'}</div>
                    </td>
                    <td className="p-4 font-black text-blue-700">{formatAmount({ amount_cents: voucher.amount_cents })}</td>
                    <td className="p-4">
                      <span className={`px-3 py-1 rounded-full border text-[10px] font-black uppercase ${statusPill(voucher.status)}`}>
                        {voucherStatus(voucher.status) || '—'}
                      </span>
                    </td>
                    <td className="p-4 text-xs font-bold text-slate-500">{formatDate(voucher.expires_at, true)}</td>
                    <td className="p-4 text-xs font-bold text-slate-500">{voucher.redeemed_at ? formatDate(voucher.redeemed_at) : '—'}</td>
                    <td className="p-4 text-right font-mono text-xs text-slate-500">{shortId(voucher.order_id)}</td>
                    <td className="p-4 text-right">
                      <div className="flex flex-wrap justify-end gap-2">
                        {voucher.order_id && (
                          <button
                            onClick={() => handleOpenOrderDetails(voucher.order_id)}
                            className="inline-flex items-center gap-1 bg-white border border-slate-200 text-slate-700 px-3 py-2 rounded-xl text-[10px] font-black uppercase hover:bg-slate-50"
                          >
                            <FaEye /> {tx('details')}
                          </button>
                        )}
                        {canCancelVoucher(voucher) && (
                          <button
                            onClick={() => handleCancelVoucher(voucher)}
                            disabled={Boolean(actionLoading)}
                            className="inline-flex items-center gap-1 bg-red-50 text-red-700 border border-red-100 px-3 py-2 rounded-xl text-[10px] font-black uppercase hover:bg-red-100 disabled:opacity-50"
                          >
                            <FaBan /> {tx('cancel')}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <SectionCard title={tx('recentMerchantValidations')} subtitle={tx('recentMerchantValidationsSubtitle')}>
        {filteredRedemptions.length === 0 ? (
          <EmptyState text={tx('noValidationFound')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[880px]">
              <thead className="bg-slate-50 text-slate-500 border-b border-slate-100">
                <tr>
                  <th className="p-4 text-xs font-black uppercase">{tx('date')}</th>
                  <th className="p-4 text-xs font-black uppercase">{tx('voucher')}</th>
                  <th className="p-4 text-xs font-black uppercase">{tx('status')}</th>
                  <th className="p-4 text-xs font-black uppercase">Source</th>
                  <th className="p-4 text-xs font-black uppercase">{tx('validatedBy')}</th>
                  <th className="p-4 text-xs font-black uppercase text-right">{tx('order')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredRedemptions.map((row, index) => (
                  <tr key={row.id || `${row.voucher_id}-${index}`} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="p-4 text-xs font-bold text-slate-500">{formatDate(row.redeemed_at || row.created_at)}</td>
                    <td className="p-4">
                      <div className="font-mono font-black text-slate-800">{row.voucher_code || shortId(row.voucher_id)}</div>
                      <div className="text-[10px] font-mono text-slate-400">{shortId(row.voucher_id)}</div>
                    </td>
                    <td className="p-4 text-xs font-black text-slate-600">{voucherStatus(row.previous_status) || '—'} → {voucherStatus(row.new_status || 'redeemed')}</td>
                    <td className="p-4"><span className="px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-[10px] font-black uppercase">{scanSource(row.source || 'edge_function')}</span></td>
                    <td className="p-4 font-mono text-xs text-slate-500">{shortId(row.redeemed_by)}</td>
                    <td className="p-4 text-right font-mono text-xs text-slate-500">{shortId(row.order_id)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {detailsOpen && orderDetails && (
        <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm p-4 flex items-center justify-center">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl max-h-[88vh] overflow-hidden border border-slate-200">
            <div className="p-6 bg-slate-950 text-white flex items-start justify-between gap-4">
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-blue-200">Détails opérationnels</p>
                <h3 className="text-2xl font-black mt-1">Commande {shortId(orderDetails.order?.id)}</h3>
                <p className="text-sm text-slate-300 font-bold mt-2">{orderDetails.order?.customer_email || 'Email client non disponible'} · {formatAmount(orderDetails.order || {})}</p>
              </div>
              <button onClick={() => setDetailsOpen(false)} className="bg-white/10 hover:bg-white/20 px-4 py-2 rounded-xl font-black text-xs uppercase">{tx('close')}</button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(88vh-120px)] space-y-6">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Paiement</p>
                  <p className="font-black text-slate-900 mt-1">{orderDetails.order?.payment_status || orderDetails.order?.status || '—'}</p>
                  <p className="text-xs text-slate-500 font-bold mt-1">Payé le {formatDate(orderDetails.order?.paid_at)}</p>
                </div>
                <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Stripe</p>
                  <p className="font-mono text-xs text-slate-700 mt-1">Session: {shortId(orderDetails.order?.stripe_session_id)}</p>
                  <p className="font-mono text-xs text-slate-700 mt-1">PI: {shortId(orderDetails.order?.stripe_payment_intent_id)}</p>
                </div>
                <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Actions rapides</p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {orderDetails.order?.id && isPaidLike(orderDetails.order) && (
                      <button onClick={() => handleRegenerateVouchers(orderDetails.order?.id)} className="bg-blue-600 text-white px-3 py-2 rounded-xl text-[10px] font-black uppercase"><FaWrench className="inline mr-1" /> Vouchers</button>
                    )}
                    {orderDetails.order?.id && orderDetails.order?.customer_email && isPaidLike(orderDetails.order) && (
                      <button onClick={() => handleResendReceipt(orderDetails.order as AdminOrderRow)} className="bg-slate-900 text-white px-3 py-2 rounded-xl text-[10px] font-black uppercase"><FaPaperPlane className="inline mr-1" /> Reçu</button>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-black text-slate-900 mb-3">Items</h4>
                {(orderDetails.items || []).length === 0 ? <EmptyState text="Aucun item trouvé pour cette commande." /> : (
                  <div className="overflow-x-auto border border-slate-100 rounded-2xl">
                    <table className="w-full text-left min-w-[720px]"><tbody>{(orderDetails.items || []).map((item: any, index: number) => (
                      <tr key={item.id || index} className="border-b border-slate-50"><td className="p-3 font-black text-sm text-slate-800">{item.title || item.product_title || item.name || 'Item Forfeo'}</td><td className="p-3 text-xs font-bold text-slate-500">Qté {item.quantity || 1}</td><td className="p-3 text-right font-black text-green-700">{formatAmount({ amount_cents: item.total_cents || item.amount_cents || item.unit_amount_cents })}</td></tr>
                    ))}</tbody></table>
                  </div>
                )}
              </div>

              <div>
                <h4 className="font-black text-slate-900 mb-3">Vouchers</h4>
                {(orderDetails.vouchers || []).length === 0 ? <EmptyState text="Aucun voucher trouvé pour cette commande." /> : (
                  <div className="overflow-x-auto border border-slate-100 rounded-2xl">
                    <table className="w-full text-left min-w-[760px]"><tbody>{(orderDetails.vouchers || []).map((voucher) => (
                      <tr key={voucher.id} className="border-b border-slate-50"><td className="p-3 font-mono font-black text-slate-800">{voucher.voucher_code || shortId(voucher.id)}</td><td className="p-3"><span className={`px-3 py-1 rounded-full border text-[10px] font-black uppercase ${statusPill(voucher.status)}`}>{voucherStatus(voucher.status) || '—'}</span></td><td className="p-3 text-xs font-bold text-slate-500">Utilisé: {formatDate(voucher.redeemed_at)}</td><td className="p-3 text-right">{canCancelVoucher(voucher) && <button onClick={() => handleCancelVoucher(voucher)} className="bg-red-50 text-red-700 border border-red-100 px-3 py-2 rounded-xl text-[10px] font-black uppercase"><FaBan className="inline mr-1" /> Annuler</button>}</td></tr>
                    ))}</tbody></table>
                  </div>
                )}
              </div>

              <div className="grid lg:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-black text-slate-900 mb-3">Validations marchand</h4>
                  {(orderDetails.redemption_logs || []).length === 0 ? <EmptyState text="Aucune validation enregistrée." /> : (
                    <div className="space-y-2">{(orderDetails.redemption_logs || []).map((log: any) => <div key={log.id} className="p-3 rounded-2xl bg-slate-50 border border-slate-100 text-xs font-bold text-slate-600">{formatDate(log.redeemed_at || log.created_at)} · {log.voucher_code || shortId(log.voucher_id)} · {log.source || 'edge_function'}</div>)}</div>
                  )}
                </div>
                <div>
                  <h4 className="font-black text-slate-900 mb-3">Actions admin</h4>
                  {(orderDetails.admin_actions || []).length === 0 ? <EmptyState text="Aucune action admin enregistrée." /> : (
                    <div className="space-y-2">{(orderDetails.admin_actions || []).map((action: any) => <div key={action.id} className="p-3 rounded-2xl bg-slate-50 border border-slate-100 text-xs font-bold text-slate-600">{formatDate(action.created_at)} · {action.action_type} · {action.admin_email || 'admin'}</div>)}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export { AdminOrdersVouchers };
export default AdminOrdersVouchers;
